/**
 * Typewriter Hook
 * 
 * Displays text at a natural reading pace, independent of how fast
 * the data arrives from the network.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

interface UseTypewriterOptions {
  /** Characters per second (default: 40 - readable typing speed) */
  speed?: number;
  /** Whether to start typing immediately when content arrives */
  autoStart?: boolean;
}

interface UseTypewriterReturn {
  /** The text currently being displayed */
  displayedText: string;
  /** Whether the typewriter is still animating */
  isTyping: boolean;
  /** Add more text to the queue */
  addText: (text: string) => void;
  /** Reset the typewriter */
  reset: () => void;
  /** Skip to show all queued text immediately */
  skipToEnd: () => void;
  /** The full text (displayed + queued) */
  fullText: string;
}

export function useTypewriter(options: UseTypewriterOptions = {}): UseTypewriterReturn {
  const { speed = 55, autoStart = true } = options; // ~55 chars/sec for faster animation
  
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  const queueRef = useRef('');
  const fullTextRef = useRef('');
  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateRef = useRef(0);
  const charIndexRef = useRef(0);

  // Calculate milliseconds per character
  const msPerChar = 1000 / speed;

  const tick = useCallback(() => {
    const now = performance.now();
    const elapsed = now - lastUpdateRef.current;
    
    // Calculate how many characters to add based on elapsed time
    const charsToAdd = Math.floor(elapsed / msPerChar);
    
    if (charsToAdd > 0 && charIndexRef.current < fullTextRef.current.length) {
      lastUpdateRef.current = now - (elapsed % msPerChar); // Keep remainder for smoother timing
      
      const newIndex = Math.min(
        charIndexRef.current + charsToAdd,
        fullTextRef.current.length
      );
      charIndexRef.current = newIndex;
      
      setDisplayedText(fullTextRef.current.slice(0, newIndex));
      
      if (charIndexRef.current < fullTextRef.current.length) {
        animationFrameRef.current = requestAnimationFrame(tick);
      } else {
        setIsTyping(false);
        animationFrameRef.current = null;
      }
    } else if (charIndexRef.current < fullTextRef.current.length) {
      animationFrameRef.current = requestAnimationFrame(tick);
    } else {
      setIsTyping(false);
      animationFrameRef.current = null;
    }
  }, [msPerChar]);

  const startTyping = useCallback(() => {
    if (animationFrameRef.current === null && charIndexRef.current < fullTextRef.current.length) {
      setIsTyping(true);
      lastUpdateRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  const addText = useCallback((text: string) => {
    fullTextRef.current += text;
    queueRef.current += text;
    
    if (autoStart) {
      startTyping();
    }
  }, [autoStart, startTyping]);

  const reset = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    queueRef.current = '';
    fullTextRef.current = '';
    charIndexRef.current = 0;
    setDisplayedText('');
    setIsTyping(false);
  }, []);

  const skipToEnd = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    charIndexRef.current = fullTextRef.current.length;
    setDisplayedText(fullTextRef.current);
    setIsTyping(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    displayedText,
    isTyping,
    addText,
    reset,
    skipToEnd,
    fullText: fullTextRef.current,
  };
}
